import PlusIcon from "@rsuite/icons/Plus";
import { useContext, useEffect, useState, type FC } from "react";
import { BsTrash3 } from "react-icons/bs";
import { Button, Checkbox, IconButton, Pagination, Table } from "rsuite";
import ProductAttributeForm from "../components/features/inventory/ProductAttributeForm";
import MainLayout from "../components/MainLayout";
import { LoadingContext } from "../contexts/LoadingContext";
import { SearchContext } from "../contexts/SearchContext";
import { ProductAttribute } from "../models/inventory/product_attribute";
import { PaginationResponse } from "../objects/pagination";
import {
  createProductAttribute,
  deleteProductAttribute,
  getProductAttributes,
  updateProductAttribute,
} from "../services/inventory/ProductAttributeApi";
import { getPagination } from "../utils/helper";
import { errorToast } from "../utils/helper-ui";
const { Column, HeaderCell, Cell } = Table;
interface ProductAttributePageProps {}

const ProductAttributePage: FC<ProductAttributePageProps> = ({}) => {
  const { search, setSearch } = useContext(SearchContext);
  const { isLoading, setIsLoading } = useContext(LoadingContext);
  const [mounted, setMounted] = useState(false);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(30);
  const [pagination, setPagination] = useState<PaginationResponse>();
  const [productAttributes, setProductAttributes] = useState<
    ProductAttribute[]
  >([]);
  const [modalFormOpen, setModalFormOpen] = useState(false);
  const [selectedProductAttribute, setSelectedProductAttribute] =
    useState<ProductAttribute>();

  const [checkedKeys, setCheckedKeys] = useState<string[]>([]);
  let checked = false;
  let indeterminate = false;

  if (checkedKeys.length === productAttributes.length) {
    checked = true;
  } else if (checkedKeys.length === 0) {
    checked = false;
  } else if (
    checkedKeys.length > 0 &&
    checkedKeys.length < productAttributes.length
  ) {
    indeterminate = true;
  }

  const handleCheckAll = (
    value: any,
    checked: boolean,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const keys = checked ? productAttributes.map((item) => item.id) : [];
    setCheckedKeys(keys);
  };
  const handleCheck = (
    value: any,
    checked: boolean,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const keys = checked
      ? [...checkedKeys, value as string]
      : checkedKeys.filter((item) => item !== (value as string));
    setCheckedKeys(keys);
  };

  useEffect(() => {
    setMounted(true);

    return () => {
      setMounted(false);
    };
  }, []);

  useEffect(() => {
    if (!mounted) return;
    fetchData();
  }, [mounted, search, page, size]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const res = await getProductAttributes({ page, size, search });
      const resJson = await res.json();
      //   console.log(data);
      setProductAttributes((prev) => [...prev, ...resJson.data.items]);
      setPagination(getPagination(resJson.data));
    } catch (error) {
      errorToast(`${error}`);
    } finally {
      setIsLoading(false);
    }
  };

  const removeProductAttribute = async (productAttribute: ProductAttribute) => {
    try {
      setIsLoading(true);
      if (
        window.confirm(
          `Are you sure you want to delete productAttribute with name ${productAttribute.name}?`
        )
      ) {
        await deleteProductAttribute(productAttribute.id!);
        fetchData();
      }
    } catch (error) {
      errorToast(`${error}`);
    } finally {
      setIsLoading(false);
    }
  };

  const saveProductAttribute = async (productAttribute: ProductAttribute) => {
    try {
      setIsLoading(true);
      if (selectedProductAttribute) {
        await updateProductAttribute(
          selectedProductAttribute.id!,
          productAttribute
        );
      } else {
        await createProductAttribute(productAttribute);
      }
      setModalFormOpen(false);
      setSelectedProductAttribute(undefined);
      fetchData();
      setProductAttributes([]);
    } catch (error) {
      errorToast(`${error}`);
    } finally {
      setIsLoading(false);
    }
  };
  const renderHeader = () => (
    <div className="flex flex-row justify-between items-center px-2">
      <h1 className="text-2xl font-semibold">Product Attribute</h1>
      {/* button */}
      <div>
        <IconButton
          size="sm"
          appearance="primary"
          color="green"
          icon={<PlusIcon />}
          onClick={() => setModalFormOpen(true)}
        >
          Create Product Attribute
        </IconButton>
      </div>
    </div>
  );
  return (
    <MainLayout header={renderHeader()}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th
                className="px-4 py-2 text-left border bg-gray-50"
                style={{ width: 100 }}
              >
                <Checkbox
                  inline
                  checked={checked}
                  indeterminate={indeterminate}
                  onChange={handleCheckAll}
                />
              </th>
              <th className="px-4 py-2 text-left border bg-gray-50">Name</th>
              <th className="px-4 py-2 text-left border bg-gray-50">
                Priority
              </th>
              <th className="px-4 py-2 text-left border bg-gray-50">Action</th>
            </tr>
          </thead>
          <tbody>
            {productAttributes.map((item, i) => (
              <tr key={item.id}>
                <td className="px-4 py-2 border">
                  <Checkbox
                    value={item.id}
                    inline
                    onChange={handleCheck}
                    checked={checkedKeys.some((e) => e === item.id)}
                  />
                  {i + 1}
                </td>

                <td className="px-4 py-2 border">
                  <div
                    className="hover:underline cursor-pointer font-semibold"
                    onClick={() => {
                      setSelectedProductAttribute(item);
                      setModalFormOpen(true);
                    }}
                  >
                    {item.name}
                  </div>
                </td>
                <td className="px-4 py-2 border">{item.priority}</td>
                <td className="px-4 py-2 border">
                  <BsTrash3
                    className="text-red-500 hover:text-red-800"
                    size={16}
                    onClick={() => removeProductAttribute(item)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!pagination?.last && (
        <div className="flex justify-center my-2">
          <Button
            onClick={() => {
              setPage((pagination?.page ?? 0) + 1);
            }}
          >
            Load more
          </Button>
        </div>
      )}
      {/* <div style={{ padding: 20 }}>
        <Pagination
          prev
          next
          first
          last
          ellipsis
          boundaryLinks
          maxButtons={5}
          size="xs"
          layout={["total", "-", "limit", "|", "pager", "skip"]}
          total={pagination?.total ?? 0}
          limitOptions={[10, 30, 50]}
          limit={pagination?.size}
          activePage={pagination?.page}
          onChangePage={setPage}
          onChangeLimit={setSize}
        />
      </div> */}
      <ProductAttributeForm
        productAttribute={selectedProductAttribute}
        open={modalFormOpen}
        onClose={() => {
          setModalFormOpen(false);
          setSelectedProductAttribute(undefined);
        }}
        onSubmit={saveProductAttribute}
      />
    </MainLayout>
  );
};
export default ProductAttributePage;
